import {
    Menu, LogOut, ChevronDown, LayoutGrid, ShoppingBag, Package, Users,
    TrendingUp, User, Settings, Store, Truck, Briefcase, ClipboardList,
    ChevronLeft, ChevronRight, Building2, CreditCard, Shield, Warehouse,
    CheckCircle, BarChart3, Sun, Moon, XCircle, FileText, Target, Download,
    ArrowDownLeft, RefreshCw
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import RoleChangeConfirmModal from '../../../components/common/Modal/RoleChangeConfirmModal';
import { getRoleDescription } from '../../../config/roles';
import { useTheme } from '../../../context/ThemeContext';
import { usePWA } from '../../../context/PWAContext';
import { countPendingUpgradeRequests } from '../../../services/api/upgradeRequestApi';

const Sidebar = ({ currentRole, availableRoles = [], onRoleChange, activeView, onViewChange, onLogout, supplierLogoUrl, companyName, subscriptionPlan }) => {
    const [isOpen, setIsOpen] = useState(true);
    const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
    const [pendingRole, setPendingRole] = useState(null);
    const [showRoleConfirm, setShowRoleConfirm] = useState(false);
    const { theme, toggleTheme, isDark } = useTheme();
    const { promptInstall, openInstallModal, isInstalled } = usePWA();
    const [pendingUpgradeCount, setPendingUpgradeCount] = useState(0);
    const [expandedGroups, setExpandedGroups] = useState({});

    // Toggle sub-menu expansion
    const toggleGroup = (groupId) => {
        setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
    };

    // Fetch pending requests badge if System Admin
    useEffect(() => {
        if (currentRole === 'SYSTEM_ADMIN') {
            countPendingUpgradeRequests()
                .then(setPendingUpgradeCount)
                .catch(() => {});
        }
    }, [currentRole, activeView]); // Refresh on view change

    // Plans that include the Analytics section. BASIC is excluded.
    const ANALYTICS_PLANS = ['STANDARD', 'PROFESSIONAL', 'ENTERPRISE'];
    const hasAnalytics = !subscriptionPlan || ANALYTICS_PLANS.includes(subscriptionPlan?.toUpperCase());

    const reportsChildren = [
        { id: 'daily-reports', label: 'Daily Reports', icon: BarChart3 },
        ...(hasAnalytics ? [{ id: 'operations-analytics', label: 'Performance', icon: TrendingUp }] : [])
    ];

    const warehouseChildren = [
        { id: 'loading-requests', label: 'Loading Request', icon: Truck },
        { id: 'offload-requests', label: 'Offload Request', icon: ArrowDownLeft },
        { id: 'transfer-history', label: 'Transfer History', icon: RefreshCw }
    ];

    const roles = availableRoles.length > 0 ? availableRoles : ['SALES_PERSON'];

    const roleIcons = {
        SYSTEM_ADMIN:     Shield,
        TENANT_OWNER:     Truck,
        TENANT_ADMIN:     Truck,
        SALES_MANAGER:    Briefcase,
        SALES_PERSON:     User,
        WAREHOUSE_PERSON: ClipboardList,
    };

    const views = {
        SYSTEM_ADMIN: [
            { id: 'dashboard', label: 'Dashboard', icon: LayoutGrid },
            { id: 'companies', label: 'Companies', icon: Building2 },
            { id: 'subscription-plans', label: 'Subscription Plans', icon: CreditCard, badge: pendingUpgradeCount },
            { id: 'staff', label: 'Staff', icon: Users },
            { id: 'analytics', label: 'Analytics', icon: TrendingUp }
        ],
        TENANT_OWNER: [
            { id: 'dashboard', label: 'Dashboard', icon: LayoutGrid },
            { id: 'orders', label: 'Orders', icon: ShoppingBag },
            { id: 'products', label: 'Inventory', icon: Package },
            { 
                id: 'warehouse-group', 
                label: 'VAN', 
                icon: Warehouse,
                children: warehouseChildren
            },
            { id: 'customers', label: 'Customers', icon: Users },
            { id: 'pos', label: 'POS Sale', icon: Store },
            { id: 'analytics', label: 'Analytics', icon: TrendingUp },
            { id: 'staff', label: 'Staff', icon: User },
            { id: 'sales-report', label: 'Operations Log', icon: ClipboardList },
            { id: 'sales-targets', label: 'Sales Targets', icon: Target },
            { 
                id: 'reports-group', 
                label: 'Reports', 
                icon: FileText,
                children: reportsChildren
            },
            { id: 'settings', label: 'Settings', icon: Settings }
        ],
        TENANT_ADMIN: [
            { id: 'dashboard', label: 'Dashboard', icon: LayoutGrid },
            { id: 'orders', label: 'Orders', icon: ShoppingBag },
            { id: 'products', label: 'Inventory', icon: Package },
            { 
                id: 'warehouse-group', 
                label: 'VAN', 
                icon: Warehouse,
                children: warehouseChildren
            },
            { id: 'customers', label: 'Customers', icon: Users },
            { id: 'analytics', label: 'Analytics', icon: TrendingUp },
            { id: 'staff', label: 'Staff', icon: User },
            { id: 'sales-report', label: 'Operations Log', icon: ClipboardList },
            { id: 'sales-targets', label: 'Sales Targets', icon: Target },
            { 
                id: 'reports-group', 
                label: 'Reports', 
                icon: FileText,
                children: reportsChildren
            },
            { id: 'settings', label: 'Settings', icon: Settings }
        ],
        SALES_MANAGER: [
            { id: 'dashboard', label: 'Dashboard', icon: LayoutGrid },
            { id: 'orders', label: 'All Orders', icon: ShoppingBag },
            { id: 'products', label: 'Inventory', icon: Package },
            { 
                id: 'warehouse-group', 
                label: 'VAN', 
                icon: Warehouse,
                children: warehouseChildren
            },
            { id: 'customers', label: 'Customers', icon: Users },
            { id: 'pos', label: 'POS Sale', icon: Store },
            { id: 'staff', label: 'Staff', icon: User },
            { id: 'sales-report', label: 'Operations Log', icon: ClipboardList },
            { id: 'sales-targets', label: 'Sales Targets', icon: Target },
            { 
                id: 'reports-group', 
                label: 'Reports', 
                icon: FileText,
                children: reportsChildren
            },
            { id: 'analytics', label: 'Analytics', icon: TrendingUp }
        ],
        SALES_PERSON: [
            { id: 'dashboard', label: 'Dashboard', icon: LayoutGrid },
            { id: 'sales-orders', label: 'Orders', icon: ShoppingBag },
            { 
                id: 'warehouse-group', 
                label: 'VAN', 
                icon: Warehouse,
                children: warehouseChildren
            },
            { id: 'pos', label: 'POS Sale', icon: Store },
            { id: 'customers', label: 'Customers', icon: Users },
            { id: 'sales-report', label: 'Ops Log', icon: ClipboardList },
            { 
                id: 'reports-group', 
                label: 'Reports', 
                icon: FileText,
                children: reportsChildren
            },
            { id: 'analytics', label: 'Analytics', icon: BarChart3 }
        ],
        WAREHOUSE_PERSON: [
            { id: 'dashboard', label: 'Dashboard', icon: LayoutGrid },
            { 
                id: 'warehouse-group', 
                label: 'Warehouse', 
                icon: Warehouse,
                children: warehouseChildren
            },
            { id: 'warehouse-orders', label: 'Orders Queue', icon: ShoppingBag },
            { id: 'warehouse-confirmed', label: 'Confirmed', icon: CheckCircle }
        ]
    };

    const handleRoleChangeRequest = (role) => {
        if (role === currentRole) { setIsRoleDropdownOpen(false); return; }
        setPendingRole(role);
        setShowRoleConfirm(true);
        setIsRoleDropdownOpen(false);
    };

    const handleRoleChangeConfirm = () => {
        if (pendingRole) {
            onRoleChange(pendingRole);
            const firstView = views[pendingRole]?.[0]?.id;
            if (firstView) onViewChange(firstView);
        }
        setShowRoleConfirm(false);
        setPendingRole(null);
    };

    const handleRoleChangeCancel = () => {
        setShowRoleConfirm(false);
        setPendingRole(null);
    };

    // ── Derived branding ───────────────────────────────────────────────────────
    // If a supplier logo is provided, show it alongside the Yaseero logo.
    // Otherwise fall back to the default Yaseero branding.
    return (
        <div className={`hidden md:flex ${isOpen ? 'w-52' : 'w-20'} bg-[var(--sidebar-from)] text-[var(--sidebar-text)] transition-all duration-300 flex-col h-screen sticky top-0 border-r border-[var(--color-border)] z-40`}>

            {/* ── Header: Simple App Icon ──────────────────────────────────────────── */}
            <div className={`relative flex items-center transition-all duration-500 ease-out border-b border-white/10 ${isOpen ? 'justify-start px-5 h-16' : 'justify-center h-16'}`}>
                
                {/* App Logo */}
                <div className="flex items-center gap-3 shrink-0">
                    <img src="/logo.jpg" alt="Logo" className="w-8 h-8 rounded-lg object-cover shrink-0 shadow-sm" />
                    
                    <span className={`font-black tracking-tight text-lg text-white transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 w-0 hidden'}`}>
                        Yaseero
                    </span>
                </div>

                {/* Sidebar Collapse Button (Desktop Only) */}
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-white dark:bg-slate-800 text-gray-500 dark:text-gray-400 rounded-full flex items-center justify-center shadow-md border border-gray-200 dark:border-slate-700 hover:scale-110 transition-transform z-50 focus:outline-none"
                    aria-label={isOpen ? "Collapse menu" : "Expand menu"}
                >
                    {isOpen ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
                </button>
            </div>

            {/* ── Navigation Links ─────────────────────────────────────────────────── */}

            {/* ── Role Switcher ─────────────────────────────────────────────── */}
            <div className={`p-3 transition-all duration-300 ${!isOpen && 'py-4'}`}>
                <div className={`bg-white/10 backdrop-blur-md rounded-xl border border-white/10 transition-all duration-300 ${isOpen ? 'p-2.5' : 'p-2 flex justify-center aspect-square items-center bg-white/5 hover:bg-white/10 border-white/20'}`}>
                    {isOpen ? (
                        <>
                            <p className="text-xs text-blue-100 font-bold mb-2 pl-1 uppercase tracking-wider opacity-80 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-white/60"></span>
                                Current Role
                            </p>
                            <div className="relative">
                                <button
                                    onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                                    className="w-full bg-black/20 text-white p-3 pl-3 pr-8 rounded-xl text-sm text-left cursor-pointer hover:bg-black/30 transition-all focus:outline-none focus:ring-2 focus:ring-white/20 border border-white/5 font-semibold flex items-center justify-between group shadow-inner"
                                >
                                    <span className="truncate">{getRoleDescription(currentRole)}</span>
                                    <ChevronDown className={`text-white/70 transition-transform duration-200 ${isRoleDropdownOpen ? 'rotate-180 text-white' : 'group-hover:text-white'}`} size={16} />
                                </button>

                                {isRoleDropdownOpen && (
                                    <div className="mt-2 bg-white rounded-xl shadow-xl overflow-hidden animate-fade-in border border-white/10">
                                        {roles.map((role) => {
                                            const RoleIcon = roleIcons[role] || Shield;
                                            return (
                                                <button
                                                    key={role}
                                                    onClick={() => handleRoleChangeRequest(role)}
                                                    className={`w-full text-left px-4 py-3 text-sm transition-colors flex items-center gap-3 border-b border-gray-100 last:border-0 ${currentRole === role
                                                        ? 'bg-blue-50 text-blue-600 font-bold'
                                                        : 'text-gray-700 hover:bg-gray-50 font-medium'
                                                        }`}
                                                >
                                                    <RoleIcon size={16} className={currentRole === role ? 'text-blue-500' : 'text-gray-400'} />
                                                    <span className="flex-1">{getRoleDescription(role)}</span>
                                                    {currentRole === role && <div className="w-2 h-2 rounded-full bg-blue-500 shadow-sm" />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div title={getRoleDescription(currentRole)} className="group relative pointer-events-none">
                            {(() => { const Icon = roleIcons[currentRole] || User; return <Icon size={24} className="text-white drop-shadow-md" />; })()}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Navigation ────────────────────────────────────────────────── */}
            <nav className="flex-1 px-2 space-y-1.5 overflow-y-auto hidden-scrollbar py-2">
                {views[currentRole]?.filter(v => v.id !== 'analytics' || hasAnalytics).map(view => {
                    const Icon = view.icon;
                    const isActive = activeView === view.id;
                    const badgeCount = view.badge || 0;
                    const isGroup = view.children && view.children.length > 0;
                    const isExpanded = expandedGroups[view.id] || false;

                    // Check if any child is active
                    const hasActiveChild = isGroup && (
                        view.children.some(child => activeView === child.id) ||
                        (view.id === 'warehouse-group' && activeView === 'warehouse-dashboard')
                    );

                    return (
                        <div key={view.id} className="space-y-1">
                            <button
                                onClick={() => {
                                    if (isGroup) {
                                        if (view.id === 'warehouse-group') {
                                            onViewChange('warehouse-dashboard');
                                        }
                                        if (!isOpen) {
                                            setIsOpen(true);
                                            setExpandedGroups(prev => ({ ...prev, [view.id]: true }));
                                        } else {
                                            toggleGroup(view.id);
                                        }
                                    } else {
                                        onViewChange(view.id);
                                    }
                                }}
                                className={`w-full flex items-center gap-3 p-2.5 rounded-xl transition-all duration-200 group relative ${isActive || hasActiveChild
                                    ? 'bg-[var(--sidebar-active-bg)] text-[var(--sidebar-active-text)] shadow-lg scale-[1.02]'
                                    : 'hover:bg-white/10 text-[var(--sidebar-text)] opacity-80 hover:opacity-100'
                                    } ${!isOpen && 'justify-center'}`}
                                title={!isOpen ? view.label : ''}
                            >
                                <Icon size={18} className={`transition-transform duration-300 ${isActive || hasActiveChild ? 'scale-110' : 'group-hover:scale-110'} shrink-0`} />
                                
                                {isOpen && <span className="font-bold tracking-wide text-sm flex-1 text-left truncate">{view.label}</span>}
                                
                                {isOpen && isGroup && (
                                    <ChevronRight size={14} className={`transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                                )}

                                {isOpen && badgeCount > 0 && (
                                    <span className="text-[10px] font-black bg-rose-500 text-white w-4 h-4 rounded-full flex items-center justify-center shrink-0 shadow-sm animate-fade-in">
                                        {badgeCount > 99 ? '99+' : badgeCount}
                                    </span>
                                )}
                                
                                {!isOpen && badgeCount > 0 && (
                                    <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-rose-500 border border-white/20"></span>
                                )}

                                {(isActive || hasActiveChild) && isOpen && (
                                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-blue-500 rounded-r-full"></div>
                                )}
                            </button>

                            {/* Sub-menu rendering */}
                            {isOpen && isGroup && isExpanded && (
                                <div className="ml-9 space-y-1 py-1 border-l border-white/10 pl-2">
                                    {view.children.map(child => {
                                        const ChildIcon = child.icon;
                                        const isChildActive = activeView === child.id;
                                        return (
                                            <button
                                                key={child.id}
                                                onClick={() => onViewChange(child.id)}
                                                className={`w-full flex items-center gap-2.5 p-2 rounded-lg transition-all text-xs font-bold ${isChildActive
                                                    ? 'text-white'
                                                    : 'text-white/60 hover:text-white hover:bg-white/5'
                                                }`}
                                            >
                                                <ChildIcon size={14} />
                                                <span>{child.label}</span>
                                                {isChildActive && <div className="w-1 h-1 rounded-full bg-blue-400 shadow-sm" />}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </nav>

            {/* ── Footer: Profile & Logout ─────────────────────────────────────────────── */}
            <div className="p-3 border-t border-white/10 mt-auto space-y-1">
                {/* Install PWA App Link */}
                <button
                    onClick={promptInstall}
                    className={`w-full flex items-center gap-2.5 p-2.5 rounded-xl transition-all group hover:bg-white/10 text-emerald-300 hover:text-emerald-200 ${!isOpen && 'justify-center bg-white/5'}`}
                    title={!isOpen ? (isInstalled ? 'App Installed' : 'Install App') : ''}
                >
                    <Download size={18} className="transition-transform group-hover:scale-110 shrink-0" />
                    {isOpen && <span className="font-bold text-sm truncate">{isInstalled ? 'App Installed' : 'Install App'}</span>}
                </button>

                {/* Profile Link */}
                <button
                    onClick={() => onViewChange('profile')}
                    className={`w-full flex items-center gap-2.5 p-2.5 rounded-xl transition-all group ${
                        activeView === 'profile'
                        ? 'bg-blue-600/20 text-blue-200'
                        : 'hover:bg-white/10 text-white/70 hover:text-white'
                    } ${!isOpen && 'justify-center bg-white/5'}`}
                    title={!isOpen ? 'My Profile' : ''}
                >
                    <User size={18} className={`transition-transform ${activeView === 'profile' ? 'scale-110' : 'group-hover:scale-110'}`} />
                    {isOpen && <span className="font-bold text-sm">My Profile</span>}
                </button>

                {/* Logout */}
                <button
                    onClick={onLogout}
                    className={`w-full flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-red-500/20 transition-all text-red-200 hover:text-white group ${!isOpen && 'justify-center bg-white/5 hover:bg-red-500/20'}`}
                    title={!isOpen ? 'Logout' : ''}
                >
                    <LogOut size={18} className="transition-transform group-hover:-translate-x-1" />
                    {isOpen && <span className="font-bold text-sm">Logout</span>}
                </button>
            </div>

            <RoleChangeConfirmModal
                isOpen={showRoleConfirm}
                onConfirm={handleRoleChangeConfirm}
                onCancel={handleRoleChangeCancel}
                newRole={getRoleDescription(pendingRole)}
            />
        </div>
    );
};

export default Sidebar;
